/**
 * Translation utilities with fallback support
 */

import { logger } from '@/lib/logger';

export interface TranslationOptions {
  /** Default fallback value when translation is missing */
  fallback?: string;
  /** Variables to interpolate in the translation string */
  vars?: Record<string, string | number>;
  /** Whether to always return fallback if translation function is not available */
  requireFallback?: boolean;
}

/**
 * Safe translation function with fallback support
 *
 * @param t - Translation function (from i18n)
 * @param key - Translation key
 * @param options - Translation options including fallback and variables
 * @returns Translated string or fallback
 */
export function safeTranslate(
  t:
    | ((key: string, options?: Record<string, unknown>) => string)
    | null
    | undefined,
  key: string,
  options?: TranslationOptions
): string {
  const { fallback, vars = {}, requireFallback = false } = options || {};

  // If no translation function provided or fallback is required
  if (!t || requireFallback) {
    if (fallback) {
      return interpolateVariables(fallback, vars);
    }
    // Generate a fallback from the key
    return generateFallbackFromKey(key);
  }

  try {
    // Attempt to get translation
    const translation = t(key, vars);

    // Check if translation returned the key itself (indicating missing translation)
    if (translation === key) {
      if (fallback) {
        return interpolateVariables(fallback, vars);
      }
      // Generate fallback from key
      return interpolateVariables(generateFallbackFromKey(key), vars);
    }

    return translation;
  } catch (error) {
    logger.warn(`Translation failed for key "${key}"`, { error });

    if (fallback) {
      return interpolateVariables(fallback, vars);
    }

    return interpolateVariables(generateFallbackFromKey(key), vars);
  }
}

/**
 * Detect strings that are translation keys rather than human-readable text.
 *
 * The backend answers with translation keys (`validation.auth.credentials.invalid`)
 * in the `message` field of a validation error, so a key with no locale entry would
 * otherwise be shown to the user verbatim. Callers use this to fall back to a
 * generic sentence instead. `scripts/check_translations.py` is what keeps the keys
 * present; this is the last line of defence if one ever slips through.
 *
 * @param value - Candidate message
 * @returns True when the value looks like a dotted i18n key
 */
export function isTranslationKey(value: string): boolean {
  return /^[a-z][A-Za-z0-9]*(?:\.[A-Za-z0-9]+)+$/.test(value.trim());
}

/**
 * Interpolate variables into a template string
 *
 * @param template - Template string with {{variable}} placeholders
 * @param vars - Variables to interpolate
 * @returns String with variables replaced
 */
function interpolateVariables(
  template: string,
  vars: Record<string, string | number>
): string {
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return vars[key]?.toString() || match;
  });
}

/**
 * Generate a human-readable fallback from a translation key
 *
 * @param key - Translation key (e.g., 'api.errors.badRequest.message')
 * @returns Human-readable fallback string
 */
function generateFallbackFromKey(key: string): string {
  // Extract the last part of the key
  const parts = key.split('.');
  const lastPart = parts[parts.length - 1];

  // Convert camelCase to readable text
  const readable = lastPart
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();

  // Common mappings for better fallbacks
  const commonMappings: Record<string, string> = {
    badRequest: 'Bad Request',
    unauthorized: 'Unauthorized',
    forbidden: 'Forbidden',
    notFound: 'Not Found',
    validation: 'Validation Error',
    rateLimited: 'Too Many Requests',
    serverError: 'Server Error',
    serviceUnavailable: 'Service Unavailable',
    networkError: 'Network Error',
    requestCancelled: 'Request Cancelled',
    clientError: 'Client Error',
    unknownError: 'Error',
    fileSizeExceedsMaximum: 'File size exceeds maximum limit',
    unsupported: 'Unsupported file type',
    documentTooShort: 'Document text appears to be too short',
    noEmailFound: 'No email address found',
    noPhoneFound: 'No phone number found',
    validationAborted: 'Validation was aborted',
    textExtraction: 'Text extraction',
    languageDetection: 'Language detection',
    metadataExtraction: 'Metadata extraction',
  };

  return commonMappings[lastPart] || readable || 'Unknown error';
}

/**
 * Create a translation function with built-in fallback support
 *
 * @param t - Base translation function
 * @param defaultFallbacks - Default fallback messages for common keys
 * @returns Enhanced translation function with fallback support
 */
export function createTranslationFunction(
  t: (key: string, options?: Record<string, unknown>) => string,
  defaultFallbacks: Record<string, string> = {}
) {
  return (
    key: string,
    options?: TranslationOptions & Record<string, unknown>
  ) => {
    const { fallback, vars, requireFallback } = options || {};

    const effectiveFallback = fallback || defaultFallbacks[key];

    return safeTranslate(t, key, {
      fallback: effectiveFallback,
      vars: vars as Record<string, string | number>,
      requireFallback,
    });
  };
}

/**
 * Common error message fallbacks
 */
export const ERROR_FALLBACKS = {
  'api.errors.badRequest.message':
    'Invalid request. Please check your input and try again.',
  'api.errors.unauthorized.message': 'Please log in to access this resource.',
  'api.errors.forbidden.message':
    'You do not have permission to access this resource.',
  'api.errors.notFound.message': 'The requested resource was not found.',
  'api.errors.validation.message': 'The provided data is invalid.',
  'api.errors.rateLimited.message':
    'Please wait before making another request.',
  'api.errors.serverError.message': 'An internal server error occurred.',
  'api.errors.serviceUnavailable.message':
    'The service is temporarily unavailable. Please try again later.',
  'api.errors.networkError.message':
    'Unable to connect to the server. Please check your internet connection and try again.',
  'api.errors.requestCancelled.message': 'The request was cancelled.',
  'api.errors.unknownError.message':
    'An unexpected error occurred. Please try again.',
  'fileUpload.errors.fileSizeExceedsMaximum':
    'File size {{actualSize}} exceeds maximum of {{maxSize}}',
  'fileType.unsupported':
    'File type {{type}} is not supported. Accepted types: {{types}}',
  'processing.steps.textExtraction.error': 'Text extraction failed: {{error}}',
  'processing.steps.languageDetection.error':
    'Language detection failed: {{error}}',
  'processing.steps.metadataExtraction.error':
    'Metadata extraction failed: {{error}}',
  'processing.steps.validation.error': 'Validation failed: {{error}}',
  'processing.validation.documentTooShort':
    'Document text appears to be too short',
  'processing.validation.noEmailFound': 'No email address found',
  'processing.validation.noPhoneFound': 'No phone number found',
  'processing.validation.validationAborted': 'Validation was aborted',
} as const;
